// Angular internal Modules import
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ReactiveFormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';

// Self created imports
import { AuthenticationModule } from './modules/authentication/authentication.module';
import { ServicesModule } from './services/services.module';
import { CoreModule } from './modules/core/core.module';

// Self created store imports
import { AuthenticationEffects } from './store/authentication/authentication.effects';
import { reducers } from './store/reducer';
import { HttpHeaderInterceptor } from './interceptors/http-header.interceptor';
import { HttpErrorInterceptor } from './interceptors/http-error.interceptor';

// Other package imports
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { AppInitializerService } from './services/appInitializer/app-initializer.service';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
// primeng imports
import { MenubarModule } from 'primeng/menubar';
import { ButtonModule } from 'primeng/button';
import { ImageModule } from 'primeng/image';

import { CourseCategoryEffects } from './store/calendar/courseCategory.effects';
import { CourseEffects } from './store/calendar/course.effects';
import { RoomEffects } from './store/calendar/room.effects';
import { InstructorEffects } from './store/calendar/instructor.effects';
import { TrainingLevelEffects } from './store/calendar/trainingLevel.effects';
import { ContingentBundleEffects } from './store/contingent/contingent-bundle.effects';
import { environment } from '../environments/environment';
import { EventEffects } from './store/calendar/event.effects';
import { UserManagementModule } from './modules/user-management/user-management.module';
import { ToastModule } from 'primeng/toast';
import { CustomizationEffects } from './store/customization/customization.effects';
import { SharedModule } from './modules/shared/shared.module';
import { LetDirective } from '@ngrx/component';
import { UserManagementEffects } from './store/user-management/user-management.effects';

export function HttpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http);
}

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        ReactiveFormsModule,
        // Self created Module imports
        AuthenticationModule,
        ServicesModule,
        CoreModule,
        SharedModule,
        // Other package imports
        FontAwesomeModule,
        StoreRouterConnectingModule.forRoot(),
        StoreModule.forRoot(reducers),
        StoreDevtoolsModule.instrument({
            name: 'Skedflex',
            logOnly: environment.production
        }),
        EffectsModule.forRoot(
            AuthenticationEffects,
            CourseCategoryEffects,
            CourseEffects,
            RoomEffects,
            InstructorEffects,
            TrainingLevelEffects,
            ContingentBundleEffects,
            EventEffects,
            CustomizationEffects,
            UserManagementEffects
        ),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            },
            defaultLanguage: 'de'
        }),
        HttpClientModule,
        // primeng imports
        MenubarModule,
        ButtonModule,
        ImageModule,
        UserManagementModule,
        ToastModule,
        LetDirective
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpHeaderInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (appInitializer: AppInitializerService) => () => appInitializer.initializeApp(),
            deps: [AppInitializerService],
            multi: true
        }
    ],
    exports: [TranslateModule],
    bootstrap: [AppComponent]
})
export class AppModule {}
